import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CgFileDocument } from "react-icons/cg";
import { TiDocumentText } from "react-icons/ti";
import { PropagateLoader } from "react-spinners";
import { MdOutlineRequestQuote } from "react-icons/md";
import { useParams, useNavigate } from "react-router-dom";
import { FaFileContract, FaScaleUnbalanced } from "react-icons/fa6";
import { HiMiniClipboardDocument, HiOutlineDocumentMagnifyingGlass } from "react-icons/hi2";
import AccesoDF from "../../../molecules/DF/AcessoDF";
import DFMenuHeader from "../../../organisms/clientes/empresas/headers/df/DFMenuHeader";
import { GET_CASOS_BY_ID } from "../../../../api/apiCasos";
import { GET_ENTERPRISE_BY_ID } from "../../../../api/apiEmpresas";
import { CREATE_JUICIO, GET_JUICIO_BY_CASO } from "../../../../api/apiJuicio";
import { CREATE_RECURSO, GET_RECURSO_BY_CASO } from "../../../../api/apiRecurso";
import { GET_CONTRACTUAL_DOCS_BY_CASO } from "../../../../api/apiContractualDocs";
import { CREATE_FICHA_TECNICA, GET_FICHA_TECNICA_BY_CASO } from "../../../../api/apiFichaTecnica";
import { CREATE_FISCALIZACION, GET_FISCALIZACION_BY_CASO } from "../../../../api/apiFiscalizacion";
import { useSession } from "../../../../context/SessionContext";
import moment from "moment";

const DFMenuTemplate = ({ id }) => {
  const navigate = useNavigate();
  const { user } = useSession();
  const { caso_id } = useParams();
  const [caso, setCaso] = useState();
  const [empresa, setEmpresa] = useState();
  const [loading, setLoading] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [status, setStatus] = useState({ fiscalizacion: false, recurso: false, juicio: false, contracts: false, fc: false });
  const [exists, setExists] = useState({ fiscalizacion: false, recurso: false, juicio: false, contracts: false, fc: false });
  const [creating, setCreating] = useState({ fiscalizacion: false, recurso: false, juicio: false, contracts: false, fc: false });

  const getCasos = useCallback(async () => {
    try {
      setLoading(true);
      const casoData = await GET_CASOS_BY_ID(caso_id);
      setCaso(casoData);
    } catch (error) {
      console.error(error);
      toast.error("No se pudo obtener el caso.");
    } finally {
      setLoading(false);
    }
  }, [caso_id]);

  const getFolders = useCallback(async () => {
    setLoadingFolders(true);

    const normalizeOne = (val) => {
      if (!val) return null;
      const v = Array.isArray(val) ? val[0] : val;
      return v ?? null;
    };

    try {
      const [rJuicio, rFiscal, rRecurso, rContracts, rFc] = await Promise.allSettled([
        GET_JUICIO_BY_CASO(caso_id),
        GET_FISCALIZACION_BY_CASO(caso_id),
        GET_RECURSO_BY_CASO(caso_id),
        GET_CONTRACTUAL_DOCS_BY_CASO(caso_id),
        GET_FICHA_TECNICA_BY_CASO(caso_id),
      ]);

      const juicio        = normalizeOne(rJuicio.status  === "fulfilled" ? rJuicio.value  : null);
      const fiscalizacion = normalizeOne(rFiscal.status  === "fulfilled" ? rFiscal.value  : null);
      const recurso       = normalizeOne(rRecurso.status === "fulfilled" ? rRecurso.value : null);
      const contracts     = normalizeOne(rContracts.status === "fulfilled" ? rContracts.value : null);
      const fc            = normalizeOne(rFc.status === "fulfilled" ? rContracts.value : null);

      setStatus({
        juicio: juicio?.status ?? null,
        fiscalizacion: fiscalizacion?.status ?? null,
        recurso: recurso?.status ?? null,
        contracts: contracts?.status ?? null,
        fc: fc?.status ?? null,
      });

      const truthy = (x) => x != null && (typeof x !== "object" || Object.keys(x).length > 0);

      setExists({
        juicio: truthy(juicio),
        fiscalizacion: truthy(fiscalizacion),
        recurso: truthy(recurso),
        contracts: truthy(contracts),
        fc: truthy(fc),
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingFolders(false);
    }
  }, [caso_id]);

  useEffect(() => {
    getCasos();
  }, [getCasos]);

  useEffect(() => {
    getFolders();
  }, [getFolders]);

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const empresaData = await GET_ENTERPRISE_BY_ID(id);
        setEmpresa(empresaData);
      } catch (error) {
        console.error(error);
        toast.error("No se pudo obtener la empresa.");
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [id]);

  const handleCreateFiscalizacion = async () => {
    try {
      setCreating((s) => ({ ...s, fiscalizacion: true }));
      await CREATE_FISCALIZACION({ status:"Iniciado", taxMatterId: caso_id });
      toast.success("Fiscalización creada.");
      setExists((s) => ({ ...s, fiscalizacion: true }));
      navigate("fiscalizacion");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo crear la Fiscalización.");
    } finally {
      setCreating((s) => ({ ...s, fiscalizacion: false }));
    }
  };

  const handleCreateRecurso = async () => {
    try {
      setCreating((s) => ({ ...s, recurso: true }));
      await CREATE_RECURSO({ status:"Iniciado", taxMatterId: caso_id });
      toast.success("Recurso creado.");
      setExists((s) => ({ ...s, recurso: true }));
      navigate("recurso");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo crear el Recurso.");
    } finally {
      setCreating((s) => ({ ...s, recurso: false }));
    }
  };

  const handleCreateJuicio = async () => {
    try {
      setCreating((s) => ({ ...s, juicio: true }));
      await CREATE_JUICIO({ status:"Iniciado", taxMatterId: caso_id });
      toast.success("Juicio creado.");
      setExists((s) => ({ ...s, juicio: true }));
      navigate("juicio");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo crear el Juicio.");
    } finally {
      setCreating((s) => ({ ...s, juicio: false }));
    }
  };

  const handleCreateFc = async () => {
    try {
      setCreating((s) => ({ ...s, fc: true }));
      await CREATE_FICHA_TECNICA({ taxMatterId: caso_id });
      toast.success("Ficha tecnica creada.");
      setExists((s) => ({ ...s, fc: true }));
      navigate("fc");
    } catch (e) {
      console.error(e);
      toast.error("No se pudo crear la ficha tecnica.");
    } finally {
      setCreating((s) => ({ ...s, fc: false }));
    }
  };

  if (loading || loadingFolders) { return (<div className="flex h-screen w-full justify-center items-center"> <PropagateLoader color="red" /> </div>)};

  return (
    <div>
      <DFMenuHeader empresa={empresa} caso={caso} />
                      <div className="py-1 gap-1 flex flex-col font-bold">
                          <div className="flex gap-2">
                              <div className="p-2 rounded-lg bg-orange-700 text-white"> {caso?.authority} </div>
                              <div className="p-2 rounded-lg bg-purple-700 text-white"> {caso?.origin} </div>
                              {caso?.concept?.map((item, index) => (
                                  <div
                                  key={index}
                                  className="p-2 text-xs bg-indigo-700 text-white rounded-md"
                                  >
                                  {item}
                                  </div>
                              ))}
                              <div className="p-2 rounded-lg bg-emerald-700 text-white"> {caso?.state} </div>
                              {caso?.comment && <div className="p-2 rounded-lg bg-gray-700 text-white"> {caso?.comment} </div>}
                              <div className="p-2 rounded-lg bg-fuchsia-700 text-white"> {moment(caso?.startPeriod).format("DD/MM/YY")} - {moment(caso?.endPeriod).format("DD/MM/YY")} </div>
                          </div>
                          <div className="p-2 w-fit rounded-lg bg-lime-700 text-white"> {caso?.status} </div>
                      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <AccesoDF
          icon={HiOutlineDocumentMagnifyingGlass}
          label="Procedimiento de fiscalización"
          label2={status.fiscalizacion || null}
          href={exists.fiscalizacion ? "fiscalizacion" : undefined}
          onCreate={!exists.fiscalizacion && user?.role !== "Client" ? handleCreateFiscalizacion : undefined}
          loading={creating.fiscalizacion}
          confirmOnCreate
        />
        <AccesoDF
          icon={HiMiniClipboardDocument}
          label="Recurso administrativo"
          label2={status.recurso || null}
          href={exists.recurso ? "recurso" : undefined}
          onCreate={!exists.recurso && user?.role !== "Client" ? handleCreateRecurso : undefined}
          loading={creating.recurso}
          confirmOnCreate
        />
        <AccesoDF
          icon={FaScaleUnbalanced}
          label="Juicio contencioso administrativo"
          label2={status.juicio || null}
          href={exists.juicio ? "juicio" : undefined}
          onCreate={!exists.juicio && user?.role !== "Client" ? handleCreateJuicio : undefined}
          loading={creating.juicio}
          confirmOnCreate
        />
        <AccesoDF
          icon={CgFileDocument}
          label="Ficha Tecnica"
          label2={exists.fc ? "Plantilla" : null}
          href={exists.fc ? "fc" : undefined}
          onCreate={!exists.fc && user?.role !== "Client" ? handleCreateFc : undefined}
          loading={creating.fc}
          confirmOnCreate
        />
        <AccesoDF
          icon={FaFileContract}
          label="Contrato de prestación de servicios"
          label2="Documentos"
          href={exists.contracts ? "contract" : undefined}
          onCreate={undefined}
          loading={creating.contracts}
          confirmOnCreate
        />
        <AccesoDF
          icon={MdOutlineRequestQuote}
          label="Cotización"
          label2="Documentos"
          href={exists.contracts ? "cotizacion" : undefined}
          onCreate={undefined}
          loading={creating.contracts}
          confirmOnCreate
        />
        <AccesoDF
          icon={TiDocumentText}
          label="Minutas"
          label2="Documentos"
          href={exists.contracts ? "minutas" : undefined}
          onCreate={undefined}
          loading={creating.contracts}
          confirmOnCreate
        />
      </div>
    </div>
  );
};

export default DFMenuTemplate;
